import React, { useState } from 'react';
import { X, Image } from 'lucide-react';
import { useProjectPanelStore } from '../../../store/projectPanelStore';
import { useAssetUrl } from '../../../hooks/useAssetUrl';
import { cn } from '../../../utils/cn';
import { getProjectRoot } from '../../../services/FileSystemService';

interface TexturePropertyEditorProps {
  value: string | null;
  onChange: (path: string | null) => void;
  onTextureMeta?: (width: number, height: number) => void;
}

export function parsePathSegments(path: string): string[] {
  return path
    .replace(/\\/g, '/')
    .split('/')
    .filter((s) => s.length > 0 && s !== '.');
}

export async function loadImageDimensions(
  assetPath: string
): Promise<{ width: number; height: number }> {
  const root = getProjectRoot();
  if (!root) throw new Error('No project root');

  const segments = parsePathSegments(assetPath);
  const fileName = segments.pop()!;
  let dir: FileSystemDirectoryHandle = root;
  for (const seg of segments) {
    dir = await dir.getDirectoryHandle(seg);
  }
  const fileHandle = await dir.getFileHandle(fileName);
  const file = await fileHandle.getFile();
  const url = URL.createObjectURL(file);

  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      reject(new Error('Failed to load image'));
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

export const TexturePropertyEditor: React.FC<TexturePropertyEditorProps> = ({
  value,
  onChange,
  onTextureMeta,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const { url, loading } = useAssetUrl(value);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const assetId = e.dataTransfer.getData('text/plain');
    if (!assetId) return;

    const assets = useProjectPanelStore.getState().assets;
    const asset = assets[assetId];
    if (!asset || asset.type !== 'sprite') return;

    onChange(asset.path);

    if (onTextureMeta) {
      loadImageDimensions(asset.path)
        .then(({ width, height }) => {
          onTextureMeta(width, height);
        })
        .catch(() => {});
    }
  };

  if (value && url) {
    const fileName = value.split('/').pop() ?? value;
    return (
      <div className="flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 rounded p-1">
        <img
          src={url}
          alt=""
          className="w-8 h-8 rounded-sm object-contain bg-zinc-900 flex-shrink-0"
        />
        <span className="text-[10px] text-zinc-300 flex-1 truncate">{fileName}</span>
        <button
          className="p-0.5 rounded hover:bg-zinc-600 text-zinc-500 hover:text-red-400 flex-shrink-0"
          onClick={() => {
            onChange(null);
            if (onTextureMeta) {
              onTextureMeta(0, 0);
            }
          }}
          title="Clear texture"
        >
          <X size={10} />
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-1 border-2 border-dashed rounded py-2 transition-colors',
        dragOver
          ? 'border-blue-500 bg-blue-500/10'
          : 'border-zinc-600 hover:border-zinc-500'
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {loading ? (
        <span className="text-[10px] text-zinc-500">Loading...</span>
      ) : (
        <>
          <Image size={12} className="text-zinc-500" />
          <span className="text-[10px] text-zinc-500">Drag image here</span>
        </>
      )}
    </div>
  );
};
